import { useEffect, useMemo, useState } from "react";
import { X, Search, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AccountSettingsPanel } from "@/components/settings/AccountSettingsPanel";
import type { LLMSettings } from "@/types";

type MCPServer = {
  id: string;
  name: string;
  registry: string;
  description: string;
  serverVersion: string;
  mcpVersion: string;
};

const MOCK_SERVERS: MCPServer[] = [
  { id: "atlas", name: "Atlas Orchestrator", registry: "Atlas Registry", description: "Reasoning-first MCP server with shared toolbelt.", serverVersion: "1.4.0", mcpVersion: "2025-06-18" },
  { id: "talos", name: "Talos Ops", registry: "Talos Registry", description: "Infra/DevOps automation via MCP.", serverVersion: "0.9.2", mcpVersion: "2025-06-18" },
  { id: "lyra", name: "Lyra Research", registry: "Lyra Hub", description: "Research companion with structured outputs.", serverVersion: "0.8.7", mcpVersion: "2024-10-18" },
  { id: "aegis", name: "Aegis Sec", registry: "Aegis Security", description: "Security posture and scanning MCP server.", serverVersion: "1.1.0", mcpVersion: "2025-06-18" },
  { id: "mercury", name: "Mercury Data", registry: "Mercury Data Cloud", description: "Data access and analytics MCP layer.", serverVersion: "0.6.1", mcpVersion: "2024-10-18" },
  { id: "nova", name: "Nova Creative", registry: "Nova Registry", description: "Creative tools and content ops via MCP.", serverVersion: "0.7.5", mcpVersion: "2025-06-18" },
  { id: "echo", name: "Echo QA", registry: "QA Hub", description: "Testing and QA automation via MCP.", serverVersion: "0.4.0", mcpVersion: "2025-06-18" },
  { id: "faro", name: "Faro Observability", registry: "Observability Hub", description: "Metrics, traces, and logs through MCP.", serverVersion: "0.5.4", mcpVersion: "2024-10-18" },
  { id: "zephyr", name: "Zephyr LLMOps", registry: "LLMOps Hub", description: "LLM orchestration and evals over MCP.", serverVersion: "0.3.9", mcpVersion: "2025-06-18" },
  { id: "aurora", name: "Aurora AI", registry: "Aurora Registry", description: "AI assistance with custom toolchains.", serverVersion: "0.6.0", mcpVersion: "2025-06-18" },
];

interface MCPServerSearchModalProps {
  onClose: () => void;
  llmSettings: LLMSettings;
  onLlmSettingsChange: (settings: LLMSettings) => void;
}

export const MCPServerSearchModal = ({ onClose, llmSettings, onLlmSettingsChange }: MCPServerSearchModalProps) => {
  const [showSettings, setShowSettings] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 6;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const pageCount = Math.max(1, Math.ceil(MOCK_SERVERS.length / pageSize));
  const currentServers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return MOCK_SERVERS.slice(start, start + pageSize);
  }, [page]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-5xl my-auto rounded-xl sm:rounded-2xl border border-white/10 bg-slate-950 text-slate-50 shadow-2xl max-h-[95vh] flex flex-col">
        <div className="flex items-start justify-between gap-2 sm:gap-3 border-b border-white/10 p-3 sm:p-5 flex-shrink-0">
          <div className="space-y-0.5 sm:space-y-1 min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-cyan-300">MCP Registries</p>
            <h3 className="text-base sm:text-xl font-semibold">Browse MCP Servers</h3>
            <p className="text-xs sm:text-sm text-slate-300/90">
              Discover MCP servers from multiple registries. <span className="hidden sm:inline">Connect the ones you want to collaborate with as agents.</span>
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <p className="text-[10px] sm:text-xs text-slate-400">
                Connected Registries (6): <span className="hidden sm:inline">Atlas, Talos, Lyra, Aegis, Mercury, Nova</span><span className="sm:hidden">6 active</span>
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 sm:gap-2 text-[10px] sm:text-xs text-cyan-400 hover:text-cyan-300 h-6 sm:h-8 px-2 w-fit"
                onClick={() => setShowSettings(true)}
              >
                <Settings className="h-3 w-3" />
                <span className="hidden sm:inline">Manage Registries</span>
                <span className="sm:hidden">Manage</span>
              </Button>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close MCP server search" className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
            <X className="h-4 w-4 sm:h-5 sm:w-5 text-slate-200" />
          </Button>
        </div>

        <div className="space-y-3 sm:space-y-4 p-3 sm:p-5 overflow-y-auto flex-1 min-h-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 sm:left-3 top-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search MCP servers by name..."
              className="w-full bg-slate-900/80 pl-8 sm:pl-10 text-xs sm:text-sm text-slate-100 placeholder:text-slate-500 h-9 sm:h-10"
            />
          </div>

          <div className="grid gap-2 sm:gap-3 sm:grid-cols-2 lg:hidden">
            {currentServers.map(server => (
              <div
                key={server.id}
                className="flex flex-col justify-between rounded-lg sm:rounded-xl border border-white/10 bg-slate-900/70 p-3 sm:p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-400/40 hover:shadow-lg"
              >
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-slate-50 flex-1 min-w-0 break-words">{server.name}</p>
                    <span className="rounded-full bg-slate-800 px-1.5 py-0.5 sm:px-2 sm:py-1 text-[9px] sm:text-[11px] font-semibold text-cyan-200 whitespace-nowrap flex-shrink-0">
                      {server.registry}
                    </span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-slate-300 leading-relaxed line-clamp-2">{server.description}</p>
                  <p className="text-[9px] sm:text-[11px] text-slate-400">Server {server.serverVersion} · MCP {server.mcpVersion}</p>
                </div>
                <div className="mt-2 sm:mt-3 flex items-center justify-between gap-2">
                  <span className="text-[9px] sm:text-[11px] uppercase tracking-wide text-slate-400">Registry ready</span>
                  <Button variant="secondary" size="sm" className="bg-cyan-600 text-white hover:bg-cyan-500 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs">
                    <span className="hidden sm:inline">Add & Connect</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-hidden rounded-xl border border-white/10 bg-slate-900/70 shadow-sm lg:block">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/90 text-left text-slate-200">
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold">Name</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold">Description</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold whitespace-nowrap">Server Ver</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold whitespace-nowrap">MCP Ver</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold">Registry</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentServers.map(server => (
                    <tr key={server.id} className="border-b border-white/5 hover:bg-slate-800/40">
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-slate-50 font-medium">{server.name}</td>
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-slate-200 max-w-xs truncate">{server.description}</td>
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-slate-300">{server.serverVersion}</td>
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-slate-300">{server.mcpVersion}</td>
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-slate-200">{server.registry}</td>
                      <td className="px-3 py-2 sm:px-4 sm:py-3 text-right">
                        <Button variant="secondary" size="sm" className="bg-cyan-600 text-white hover:bg-cyan-500 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs whitespace-nowrap">
                          Add & Connect
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0 border-t border-white/10 p-3 sm:p-4 flex-shrink-0">
          <span className="text-[10px] sm:text-xs text-slate-400">
            Page {page} of {pageCount}
          </span>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {page !== 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs flex-1 sm:flex-initial"
              >
                Previous
              </Button>
            )}
            {page !== pageCount && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                className="border-cyan-500/30 bg-cyan-500/30 text-cyan-300 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs flex-1 sm:flex-initial"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>

      {showSettings && <AccountSettingsPanel onClose={() => setShowSettings(false)} defaultTab="registries" llmSettings={llmSettings} onLlmSettingsChange={onLlmSettingsChange} />}
    </div>
  );
};
